import { QueryMeta } from '@tanstack/react-query';
import { ChronospherePanelDefinition, QueryResolution, SupportedDataType } from '@/core';
import { SnapshotID } from '../DataSnapshotProvider/model';
import { GlobalFilter } from '../GlobalFilters';
import { QueryLimits } from '../QueryTruncationProvider';

// Common types that are needed across multiple data-querying utils. Pulled out
// separately to avoid circular dependencies.

export type DataQueryType = 'basic' | 'progressive';

export interface FrameworkQueryMeta extends QueryMeta {
  dataQueryType: DataQueryType;
}

export interface UseDataQueryOptions {
  suggestedStepMs?: number;
  /**
   * Impacts max datapoints per series, used when calculating the auto step interval
   */
  resolution?: QueryResolution;
  // This option is probably only going to be temporary until every panel type supports compare
  allowCompare?: boolean;
  /**
   * The data type that the query should return
   */
  dataType?: SupportedDataType;
  /**
   * Optional global filters to disable for query processing
   */
  disabledGlobalFilters?: Record<string, Array<GlobalFilter['label']>>;
  /*
   * Pagination state
   */
  pagination?: PaginationConfig;
  /*
   * Controls backend query limits
   */
  queryLimits?: QueryLimits;

  /**
   * Optional panel defintion that can be used to provide additional information to the query. This is
   * available in certain context such as Dashboards where the panel definition is available or if explicitly provided.
   */
  panelDefinition?: ChronospherePanelDefinition;

  /**
   * Determines how the data query provider will handle the query lifecycle (e.g. fetching data, caching results,
   * refetching when the time range changes).
   * - `active`: The queries will be enabled, update as expected when the time range changes, etc.
   * - `suspended`: New data will not be fetched/refetched. Previous data will be retained in the cache.
   * - `unmounted`:  New data will not be fetched/refetched. Previous data will not be retained in the cache.
   */
  queryMode?: DataQueryMode;

  /**
   * Optional snapshot ID to load data from a snapshot instead of fetching new data
   */
  snapshotId?: SnapshotID;
}

export type PaginationConfig = {
  page?: number;
  pageSize: number;
  hasNextPage?: boolean;
};

export type DataPluginQueryMethod = 'getData' | 'getComparableData';

export type DataQueryPagination = {
  page: number;
  pageSize: number;
};

export const DEFAULT_DATA_QUERY_MODE: DataQueryMode = 'active';
export const DATA_QUERY_MODES = ['active', 'suspended', 'unmounted'] as const;
export type DataQueryMode = (typeof DATA_QUERY_MODES)[number];
